import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  FeedbackCommentDto,
  FeedbackPriority,
  FeedbackReplyDto,
  FeedbackStatus,
  FeedbackType,
} from '@crm/shared'
import {
  addFeedbackComment,
  deleteFeedback,
  deleteFeedbackComment,
  getFeedback,
  updateFeedbackContent,
  updateFeedbackStatus,
} from './feedback.api'
import { useAuthStore } from '../auth/authStore'
import { Loader } from '../../shared/ui/Loader'
import { TYPE_LABELS, PRIORITY_LABELS, STATUS_LABELS, STATUS_COLORS } from './feedback.labels'
import { CommentBubble, SupportReplyBubble } from './FeedbackThreadItem'

interface Props {
  id: string
  onClose: () => void
}

/** Сообщение переписки: комментарий человека или ответ поддержки из «Пульта проектов». */
type ThreadEntry =
  | { kind: 'comment'; at: number; comment: FeedbackCommentDto & { isLegacy: boolean } }
  | { kind: 'reply'; at: number; reply: FeedbackReplyDto }

export function FeedbackDetailModal({ id, onClose }: Props) {
  const user = useAuthStore((s) => s.user)
  const isAdmin = user?.role === 'ADMIN'
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [text, setText] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [editing, setEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editDesc, setEditDesc] = useState('')
  const [editType, setEditType] = useState<FeedbackType>('BUG')
  const [editPriority, setEditPriority] = useState<FeedbackPriority>('NORMAL')

  const { data, isLoading } = useQuery({
    queryKey: ['feedback', id],
    queryFn: () => getFeedback(id),
  })

  const isAuthor = !!user && data?.authorId === user.id
  const canReply = isAdmin || isAuthor
  const canEditContent = isAdmin || isAuthor

  function openEdit() {
    if (!data) return
    setEditTitle(data.title)
    setEditDesc(data.description ?? '')
    setEditType(data.type)
    setEditPriority(data.priority)
    setEditing(true)
  }

  const statusMut = useMutation({
    mutationFn: (s: FeedbackStatus) => updateFeedbackStatus(id, s),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['feedback'] }),
  })

  const contentMut = useMutation({
    mutationFn: () =>
      updateFeedbackContent(id, {
        title: editTitle.trim(),
        description: editDesc.trim() || null,
        type: editType,
        priority: editPriority,
      }),
    onSuccess: () => {
      setEditing(false)
      void qc.invalidateQueries({ queryKey: ['feedback'] })
    },
  })

  const commentMut = useMutation({
    mutationFn: () => addFeedbackComment(id, { text: text.trim(), screenshot: file }),
    onSuccess: () => {
      setText('')
      setFile(null)
      if (fileRef.current) fileRef.current.value = ''
      void qc.invalidateQueries({ queryKey: ['feedback', id] })
    },
  })

  const delCommentMut = useMutation({
    mutationFn: (commentId: string) => deleteFeedbackComment(id, commentId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['feedback', id] }),
  })

  const delMut = useMutation({
    mutationFn: () => deleteFeedback(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['feedback'] })
      onClose()
    },
  })

  const replies: FeedbackReplyDto[] = data?.replies ?? []
  // Старый adminReply показываем первым сообщением треда, только если нет ни
  // комментариев, ни ответов поддержки — иначе ответ задвоился бы.
  const legacyComment: (FeedbackCommentDto & { isLegacy: boolean }) | null =
    data?.adminReply && data.comments.length === 0 && replies.length === 0
      ? {
          id: '__legacy__',
          feedbackId: id,
          authorId: data.authorId,
          author: { id: '__admin__', email: 'admin', name: 'Администратор', role: 'ADMIN' },
          text: data.adminReply,
          screenshot: null,
          createdAt: data.updatedAt,
          isLegacy: true,
        }
      : null

  // Переписка по времени: комментарии людей и ответы поддержки вперемешку.
  const thread: ThreadEntry[] = [
    ...(legacyComment ? [legacyComment] : []),
    ...(data?.comments ?? []).map((c) => ({ ...c, isLegacy: false })),
  ]
    .map(
      (comment): ThreadEntry => ({ kind: 'comment', at: Date.parse(comment.createdAt), comment }),
    )
    .concat(
      replies.map(
        (reply): ThreadEntry => ({ kind: 'reply', at: Date.parse(reply.createdAt), reply }),
      ),
    )
    .sort((a, b) => a.at - b.at)

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col"
      >
        {isLoading || !data ? (
          <div className="p-5 text-gray-500">Загрузка…</div>
        ) : editing ? (
          <div className="p-5 space-y-3 overflow-auto">
            <h2 className="text-base font-semibold">Редактирование</h2>
            <div className="flex gap-3">
              <label className="flex-1">
                <span className="text-xs text-gray-500">Тип</span>
                <select
                  value={editType}
                  onChange={(e) => setEditType(e.target.value as FeedbackType)}
                  className="mt-0.5 w-full border rounded px-2 py-1.5 text-sm"
                >
                  <option value="BUG">🐛 Баг</option>
                  <option value="IDEA">💡 Идея</option>
                </select>
              </label>
              <label className="flex-1">
                <span className="text-xs text-gray-500">Приоритет</span>
                <select
                  value={editPriority}
                  onChange={(e) => setEditPriority(e.target.value as FeedbackPriority)}
                  className="mt-0.5 w-full border rounded px-2 py-1.5 text-sm"
                >
                  <option value="CRITICAL">Критично</option>
                  <option value="NORMAL">Обычный</option>
                  <option value="LOW">Низкий</option>
                </select>
              </label>
            </div>
            <label>
              <span className="text-xs text-gray-500">Заголовок</span>
              <input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                maxLength={200}
                className="mt-0.5 w-full border rounded px-2 py-1.5 text-sm"
              />
            </label>
            <label>
              <span className="text-xs text-gray-500">Описание</span>
              <textarea
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                rows={4}
                maxLength={10000}
                className="mt-0.5 w-full border rounded px-2 py-1.5 text-sm"
              />
            </label>
            {contentMut.isError && (
              <div className="text-sm text-red-600">
                {(contentMut.error as Error)?.message ?? 'Не удалось сохранить'}
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => contentMut.mutate()}
                disabled={!editTitle.trim() || contentMut.isPending}
                className="px-4 py-1.5 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {contentMut.isPending && <Loader variant="bus" size={40} />}
                Сохранить
              </button>
              <button
                onClick={() => setEditing(false)}
                className="px-4 py-1.5 text-sm border rounded hover:bg-gray-50"
              >
                Отмена
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Шапка */}
            <div className="p-5 border-b flex-shrink-0">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap text-xs mb-1.5">
                    <span className="px-2 py-0.5 rounded bg-gray-100">
                      {TYPE_LABELS[data.type]}
                    </span>
                    <span className="px-2 py-0.5 rounded bg-gray-100">
                      {PRIORITY_LABELS[data.priority]}
                    </span>
                    <span className={`px-2 py-0.5 rounded ${STATUS_COLORS[data.status]}`}>
                      {STATUS_LABELS[data.status]}
                    </span>
                    {isAdmin && (
                      <select
                        value={data.status}
                        onChange={(e) => statusMut.mutate(e.target.value as FeedbackStatus)}
                        className="border rounded px-1.5 py-0.5 text-xs"
                      >
                        <option value="NEW">Новая</option>
                        <option value="IN_PROGRESS">В работе</option>
                        <option value="FIXED">Исправлено</option>
                        <option value="REJECTED">Отклонено</option>
                      </select>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-semibold truncate">{data.title}</h2>
                    {canEditContent && (
                      <button
                        onClick={openEdit}
                        title="Редактировать"
                        className="text-gray-400 hover:text-brand-600 flex-shrink-0"
                      >
                        ✎
                      </button>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    <span className="font-medium text-gray-700">{data.author?.name ?? '—'}</span>
                    {' · '}
                    <span>{data.author?.email}</span>
                    {' · '}
                    <span>{new Date(data.createdAt).toLocaleString('ru-RU')}</span>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-700 text-xl leading-none flex-shrink-0"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Тред сообщений (включая исходный пост — он скроллится вместе с ответами) */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4 min-h-0">
              {(data.description || data.screenshot) && (
                <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 space-y-2">
                  {data.description && (
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{data.description}</p>
                  )}
                  {data.screenshot && (
                    <a
                      href={`/api/uploads/feedback/${data.screenshot}`}
                      target="_blank"
                      rel="noreferrer"
                      className="block"
                    >
                      <img
                        src={`/api/uploads/feedback/${data.screenshot}`}
                        alt="скриншот"
                        className="max-h-72 rounded border"
                      />
                    </a>
                  )}
                </div>
              )}
              {thread.length === 0 ? (
                <div className="text-sm text-gray-400 text-center py-4">Пока нет ответов</div>
              ) : (
                thread.map((entry) =>
                  entry.kind === 'reply' ? (
                    <SupportReplyBubble key={`reply-${entry.reply.id}`} reply={entry.reply} />
                  ) : (
                    <CommentBubble
                      key={entry.comment.id}
                      comment={entry.comment}
                      isOwn={
                        entry.comment.authorId === user?.id ||
                        (entry.comment.author?.role === 'ADMIN' && isAdmin)
                      }
                      canDelete={isAdmin || entry.comment.authorId === user?.id}
                      onDelete={() => delCommentMut.mutate(entry.comment.id)}
                    />
                  ),
                )
              )}
            </div>

            {/* Поле ввода */}
            {canReply && (
              <div className="border-t p-4 flex-shrink-0 space-y-2">
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Написать ответ…"
                  rows={3}
                  maxLength={10000}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && text.trim()) {
                      commentMut.mutate()
                    }
                  }}
                  className="w-full border rounded px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => commentMut.mutate()}
                    disabled={!text.trim() || commentMut.isPending}
                    className="px-4 py-1.5 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    {commentMut.isPending && <Loader variant="bus" size={40} />}
                    Отправить
                  </button>
                  <label className="cursor-pointer text-sm text-gray-500 hover:text-brand-600 flex items-center gap-1">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    />
                    📎 {file ? file.name : 'Прикрепить скриншот'}
                  </label>
                  {file && (
                    <button
                      onClick={() => {
                        setFile(null)
                        if (fileRef.current) fileRef.current.value = ''
                      }}
                      className="text-xs text-red-400 hover:text-red-600"
                    >
                      ✕
                    </button>
                  )}
                  <span className="text-xs text-gray-400 ml-auto">Ctrl+Enter — отправить</span>
                </div>
                {commentMut.isError && (
                  <div className="text-xs text-red-600">
                    {(commentMut.error as Error)?.message ?? 'Ошибка при отправке'}
                  </div>
                )}
              </div>
            )}

            {/* Удаление записи */}
            {isAdmin && (
              <div className="border-t px-4 py-2 flex justify-end flex-shrink-0">
                <button
                  onClick={() => {
                    if (window.confirm('Удалить всю запись вместе с перепиской?')) delMut.mutate()
                  }}
                  className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded"
                >
                  Удалить запись
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
